import React, { useState } from 'react';
import { Tour } from '../../types/tours';
import { ChevronLeft, ChevronRight } from 'lucide-react'; 
import { useGesture } from '@use-gesture/react';

interface TourDetailGalleryProps {
  tour: Tour;
}

const TourDetailGallery: React.FC<TourDetailGalleryProps> = ({ tour }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showAllImages, setShowAllImages] = useState(false);

  const bind = useGesture({
    onDrag: ({ direction: [dx], dragging }) => {
      if (!dragging && !isAnimating) {
        if (dx > 0) {
          handlePrevious();
        } else if (dx < 0) {
          handleNext();
        }
      }
    }
  });

  const galleryImages = tour.tour_images
    ?.filter(img => img.image_type === 'gallery')
    .sort((a, b) => a.display_order - b.display_order)
    .map(img => img.image_url) || [];

  const handleThumbnailClick = (index: number) => {
    if (isAnimating || index === currentImageIndex) return;
    setIsAnimating(true);
    setCurrentImageIndex(index);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handlePrevious = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentImageIndex((prev) => 
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  };

  const handleNext = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentImageIndex((prev) => 
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
    setTimeout(() => setIsAnimating(false), 500);
  };

  if (!galleryImages.length) return null;

  return (
    <div className="space-y-4">
      <div className="relative aspect-[16/9] rounded-2xl overflow-hidden group bg-black"
        {...bind()}>
        {/* Images */}
        <div className="relative h-full">
          {/* Blurred background images */}
          {galleryImages.map((image, index) => (
            <div
              key={`bg-${index}`}
              className={`absolute inset-0 transition-opacity duration-500 ${
                currentImageIndex === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt=""
                className="w-full h-full object-cover blur-md scale-110 opacity-90"
              />
            </div>
          ))}
          {/* Main images with preserved aspect ratio */}
          {galleryImages.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Gallery image ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ${
                currentImageIndex === index ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={handlePrevious}
            disabled={isAnimating}
            aria-label="Previous image"
            className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleNext}
            disabled={isAnimating}
            aria-label="Next image"
            className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Indicators */}
        <div className="absolute bottom-4 inset-x-0 flex justify-center gap-2">
          {galleryImages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isAnimating) {
                  setIsAnimating(true);
                  setCurrentImageIndex(index);
                  setTimeout(() => setIsAnimating(false), 500);
                }
              }}
              aria-label={`Image ${index + 1} of ${galleryImages.length}`}
              className={`w-8 h-8 rounded-full transition-all ${
                currentImageIndex === index
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Thumbnails */}
      <div className="relative">
        <div className={`grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 ${
          !showAllImages && galleryImages.length > 10 ? 'max-h-24 overflow-hidden' : ''
        }`}>
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className={`relative aspect-video rounded-lg overflow-hidden bg-black group/thumb ${
                currentImageIndex === index ? 'ring-2 ring-[#E85C0D]' : ''
              }`}
            >
              {/* Blurred background */}
              <div className="absolute inset-0">
                <img
                  src={image}
                  alt=""
                  className="w-full h-full object-cover blur-sm scale-110 opacity-90"
                />
              </div>
              {/* Main thumbnail image */}
              <button
                onClick={() => handleThumbnailClick(index)}
                aria-label={`View image ${index + 1}`}
                className="absolute inset-0 w-full h-full"
                title={`View image ${index + 1}`}
              >
                <img
                  src={image}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-contain"
                />
                <div className={`absolute inset-0 bg-black/50 transition-opacity ${
                  currentImageIndex === index ? 'opacity-0' : 'opacity-0 group-hover/thumb:opacity-100'
                }`} />
              </button>
            </div>
          ))}
        </div>

        {/* Show More Button */}
        {galleryImages.length > 10 && (
          <button
            onClick={() => setShowAllImages(!showAllImages)}
            className="w-full mt-2 px-4 py-2 bg-[#2A382A]/5 text-[#2A382A] text-sm rounded-lg hover:bg-[#2A382A]/10 transition-colors">
            {showAllImages ? 'Show Less' : `Show All (${galleryImages.length} images)`}
          </button>
        )}
      </div>
    </div>
  );
};

export default TourDetailGallery;